import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatErr } from "@/lib/api";
import AppHeader from "@/components/AppHeader";
import { Users, RefreshCw, MapPin, Mail, Calendar } from "lucide-react";

export default function AdminWaitlist() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true); setErr("");
    try {
      const r = await api.get("/admin/waitlist?limit=500");
      setData(r.data);
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  useEffect(() => { if (user?.is_admin) load(); }, [user?.is_admin]);

  if (!user) return <Navigate to="/login" replace />;
  if (!user.is_admin) return <Navigate to="/dashboard" replace />;

  const items = data?.items || [];
  const byState = {};
  const byCountry = {};
  items.forEach(i => {
    if (i.state) byState[i.state] = (byState[i.state] || 0) + 1;
    if (i.country) byCountry[i.country] = (byCountry[i.country] || 0) + 1;
  });
  const topStates = Object.entries(byState).sort((a, b) => b[1] - a[1]).slice(0, 8);
  const topCountries = Object.entries(byCountry).sort((a, b) => b[1] - a[1]).slice(0, 5);

  const downloadCsv = () => {
    const rows = [["Email", "Name", "State", "Country", "Source", "Joined At"]];
    items.forEach(i => rows.push([
      i.email, i.name || "", i.state || "", i.country || "", i.source || "", i.joined_at || "",
    ]));
    const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `waitlist_${new Date().toISOString().slice(0,10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-cream" data-testid="admin-waitlist">
      <AppHeader />
      <div className="max-w-6xl mx-auto px-6 py-10">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-sage">Admin</div>
            <h1 className="font-serif text-4xl mt-1">Launch waitlist</h1>
            <p className="text-moss mt-2 text-sm">
              Anyone who signs up in demo mode is auto-added here. {data?.total ?? 0} total signups.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={load} disabled={busy}
                    className="inline-flex items-center gap-1.5 bg-ink text-white rounded-lg px-3 py-2 text-xs font-medium hover:bg-ink-deep disabled:opacity-60"
                    data-testid="admin-waitlist-refresh-btn">
              <RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} /> Refresh
            </button>
            <button onClick={downloadCsv} disabled={items.length === 0}
                    className="inline-flex items-center gap-1.5 bg-forest text-white rounded-lg px-3 py-2 text-xs font-medium hover:bg-forest-deep disabled:opacity-60"
                    data-testid="admin-waitlist-csv-btn">
              Download CSV
            </button>
          </div>
        </div>

        {err && <div className="mt-4 text-sm text-clay bg-clay-light px-4 py-3 rounded-lg" data-testid="admin-waitlist-error">{err}</div>}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8" data-testid="admin-waitlist-stats">
          <StatCard icon={Users} label="Total signups" value={data?.total ?? "—"} />
          <StatCard icon={MapPin} label="Top state" value={topStates[0]?.[0] || "—"}
                    sub={topStates[0] ? `${topStates[0][1]} signups` : ""} />
          <StatCard icon={Calendar} label="Latest signup"
                    value={items[0]?.joined_at ? new Date(items[0].joined_at).toLocaleString() : "—"} />
        </div>

        {topStates.length > 0 && (
          <div className="mt-6 bg-linen border border-sand rounded-xl p-4">
            <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-sage mb-3">By state</div>
            <div className="flex flex-wrap gap-2">
              {topStates.map(([s, c]) => (
                <span key={s} className="inline-flex items-center gap-1 bg-white border border-sand rounded-full px-3 py-1 text-xs">
                  <span className="font-mono">{s}</span><span className="text-sage">·</span><span>{c}</span>
                </span>
              ))}
            </div>
          </div>
        )}

        <div className="mt-8 bg-white border border-sand rounded-2xl overflow-hidden">
          <table className="w-full text-sm" data-testid="admin-waitlist-table">
            <thead className="bg-cream-warm text-[10px] uppercase tracking-[0.2em] text-sage">
              <tr>
                <th className="text-left py-3 px-4">Joined</th>
                <th className="text-left py-3 px-4">Name</th>
                <th className="text-left py-3 px-4">Email</th>
                <th className="text-left py-3 px-4">State</th>
                <th className="text-left py-3 px-4">Country</th>
                <th className="text-left py-3 px-4">Source</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-sand-warm">
              {items.map((i) => (
                <tr key={i.id} className="hover:bg-cream-warm" data-testid={`admin-waitlist-row-${i.id}`}>
                  <td className="py-2.5 px-4 text-[11px] font-mono text-sage">
                    {i.joined_at ? new Date(i.joined_at).toLocaleDateString() : "—"}
                  </td>
                  <td className="py-2.5 px-4">{i.name || "—"}</td>
                  <td className="py-2.5 px-4 text-ink truncate max-w-[260px]">{i.email}</td>
                  <td className="py-2.5 px-4 font-mono text-xs">{i.state || "—"}</td>
                  <td className="py-2.5 px-4 font-mono text-xs">{i.country || "—"}</td>
                  <td className="py-2.5 px-4 text-xs text-sage">{i.source || "—"}</td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr><td colSpan={6} className="py-10 text-center text-sm text-sage">No signups yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub }) {
  return (
    <div className="bg-cream-warm border border-sand rounded-xl p-4">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] font-semibold text-sage">
        <Icon className="w-3.5 h-3.5" />{label}
      </div>
      <div className="mt-1.5 font-serif text-2xl text-ink truncate">{value}</div>
      {sub && <div className="text-xs text-sage mt-0.5">{sub}</div>}
    </div>
  );
}
